import React from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import AgentModeSwitch from '../AgentModeSwitch';
import AIStatusBand from './AIStatusBand';

/**
 * The band's own height, expressed from the same tokens the band uses, so the
 * row cannot drift from it: two paddings plus one 1.3 line.
 *
 * The row needs this because its two occupants come and go independently. With
 * the band gone (pure HITL, or a status cleared by an interception) the row
 * would otherwise shrink to the switch's 26px and shunt all three panes up a
 * few pixels mid-order. Pinning the minimum keeps the strip one fixed height
 * whatever is in it.
 */
const ROW_MIN_HEIGHT = `calc(${HITL3_PADDING} * 2 + 1.3 * ${HITL3_FONT_SIZE})`;

/**
 * CONSOLE STATUS ROW — the strip under the header, above the three panes.
 *
 * Two independent occupants, side by side: the AI status band on the left and
 * the agent-mode switch on the right. They are SEPARATE blocks, not one
 * element — the band keeps its own saturated state colour and its own edges,
 * and the switch sits beside it on the page's own ground. An earlier pass put
 * the switch inside the band, floating on top of whatever colour the AI was
 * showing; it read as a patch stuck onto the status rather than a control.
 *
 * Each occupant decides for itself whether to render:
 *
 * - the band self-hides on a pure-HITL restaurant and, under 7.0, once the
 *   agent has intercepted (see useAIStatusBand's `clearedByIntercept`);
 * - the switch self-hides on the `hitl_shadow_mode` triple gate.
 *
 * That independence is the point. The band's gate goes false at the exact
 * moment the agent intercepts, which is when the switch most needs to be on
 * screen reading a locked `Intercept`. Sharing one gate would have deleted the
 * control at the one moment it describes.
 *
 * With supervised mode off this renders the bare `<AIStatusBand />` and nothing
 * else — same element, same parent, no wrapper — so the flag-off console is
 * byte-identical and no snapshot moves.
 */
const ConsoleStatusRow = () => {
  const isShadowMode = useAppSelector(selectIsShadowModeEnabled);

  if (!isShadowMode) return <AIStatusBand />;

  return (
    <div
      data-testid="console-status-row"
      // Inline rather than makeStyles: a new sheet here would renumber the
      // generated class names of every rule created after it and move unrelated
      // snapshots.
      style={{
        display: 'flex',
        // Stretch, not center: the switch takes the row's full height so the
        // two occupants are one continuous band rather than a strip with a chip
        // parked beside it.
        alignItems: 'stretch',
        flexShrink: 0,
        minHeight: ROW_MIN_HEIGHT,
        // Shows only behind the switch's own surface; with no gap and no right
        // padding there is no bare row left to see.
        backgroundColor: Colors.porcelain,
        // Deliberately flush on both sides. The band is full-bleed to the left
        // edge, so anything short of full-bleed on the right reads as the strip
        // stopping early and a separate control starting.
        gap: 0,
      }}
    >
      {/* The band's slot. Always present and always greedy, even when the band
          inside it has hidden itself — that is what keeps the switch anchored to
          the right edge instead of sliding left when a status clears. minWidth:0
          is what lets the band's own ellipsis engage. */}
      <div
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <AIStatusBand />
      </div>
      <AgentModeSwitch />
    </div>
  );
};

export default ConsoleStatusRow;
